import { Link } from "react-router-dom";

import { logError } from "@/lib/log";
import { fmtMoney } from "@/lib/money";
import { AllergenPicker, AllergenBadges, DietTagPicker } from "@/components/Allergens";
import { allergenLabel } from "@/lib/allergens";
import { UpgradeNotice } from "@/components/UpgradeNotice";
import { useCallback, useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, Reorder, useDragControls } from "framer-motion";
import { Plus, Trash2, Pencil, ImagePlus, GripVertical, Utensils, X, Loader2, Wand2, Languages, Sparkles, LayoutTemplate, Lock, Eye, EyeOff, Camera, Truck, CopyPlus, ImageOff, FolderOpen } from "lucide-react";
import { OptionBanners } from "@/components/OptionBanners";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AccentColorPicker } from "@/components/AccentColorPicker";
import { HeadingFontPicker } from "@/components/HeadingFontPicker";

const TEMPLATE_LABELS = { elegante: "Elegante", moderno: "Moderno", minimal: "Minimal", colorato: "Colorato" };

export default function MenuBuilder() {
  const { user } = useAuth();
  const feats = user?.subscription?.features || {};
  const isPaid = !!user?.is_paid;
  const canTranslate = user?.subscription?.can_translate;
  const [menus, setMenus] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState("");
  const [dishModal, setDishModal] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("menuone_ai_draft");
      if (raw) setDraft(JSON.parse(raw));
    } catch (e) { logError("Bozza AI non leggibile:", e); localStorage.removeItem("menuone_ai_draft"); }
  }, []);

  const importDraft = async () => {
    if (!draft || !activeMenu) return;
    setImporting(true);
    try {
      for (const c of (draft.categories || [])) {
        const { data: cat } = await api.post("/categories", { name: c.name, menu_id: activeMenu });
        for (const d of (c.dishes || [])) {
          await api.post("/dishes", { category_id: cat.id, name: d.name, description: d.description || "", price: Number(d.price) || 0, available: true });
        }
      }
      localStorage.removeItem("menuone_ai_draft");
      setDraft(null);
      toast.success("Menù generato con l'AI importato!");
      await load();
    } catch (e) { toast.error("Errore durante l'import del menù"); }
    finally { setImporting(false); }
  };

  const dismissDraft = () => { localStorage.removeItem("menuone_ai_draft"); setDraft(null); };

  const load = useCallback(async () => {
    try {
      const [ms, cats, ds, r] = await Promise.all([api.get("/menus"), api.get("/categories"), api.get("/dishes"), api.get("/restaurant")]);
      setMenus(ms.data);
      const wanted = new URLSearchParams(window.location.search).get("menu");
      setActiveMenu((prev) => (prev && ms.data.some((m) => m.id === prev) ? prev : (wanted && ms.data.some((m) => m.id === wanted) ? wanted : ms.data[0]?.id)));
      setCategories(cats.data);
      setDishes(ds.data);
      setRestaurant(r.data);
    } catch (e) {
      logError("Caricamento menù fallito:", e);
      toast.error("Impossibile caricare il menù. Riprova.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = menus.find((m) => m.id === activeMenu);
  const menuCategories = categories.filter((c) => c.menu_id === activeMenu);

  const addMenu = async () => {
    const name = prompt("Nome del nuovo menù", "Menù pranzo");
    if (!name) return;
    try {
      const { data } = await api.post("/menus", { name });
      setMenus((m) => [...m, data]);
      setActiveMenu(data.id);
      toast.success("Menù creato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const renameMenu = async () => {
    if (!current) return;
    const name = prompt("Rinomina menù", current.name);
    if (!name) return;
    const { data } = await api.put(`/menus/${current.id}`, { name });
    setMenus((m) => m.map((x) => (x.id === data.id ? data : x)));
  };
  const duplicateMenu = async () => {
    const name = window.prompt("Nome del nuovo menù FoodTruck (es. Sagra 18/09):", `${current.name} (copia)`);
    if (name === null) return;
    try {
      const { data } = await api.post(`/menus/${current.id}/duplicate`, { name });
      setMenus((ms) => [...ms, data]);
      setActiveMenu(data.id);
      await load();
      toast.success(`Menù "${data.name}" creato con tutti i piatti`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteMenu = async () => {
    if (!current) return;
    if (!window.confirm(`Eliminare il menù "${current.name}" con tutte le sue categorie e piatti?`)) return;
    try {
      await api.delete(`/menus/${current.id}`);
      toast.success("Menù eliminato");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeTemplate = async (template) => {
    if (!feats.templates) { toast.error("I design multipli sono disponibili con i piani Pro e Business"); return; }
    const { data } = await api.put(`/menus/${current.id}`, { name: current.name, template });
    setMenus((m) => m.map((x) => (x.id === data.id ? data : x)));
    toast.success("Design aggiornato");
  };

  const addCategory = async () => {
    if (!newCat.trim() || !activeMenu) return;
    try {
      const { data } = await api.post("/categories", { name: newCat.trim(), menu_id: activeMenu });
      setCategories((c) => [...c, data]);
      setNewCat("");
      toast.success("Categoria aggiunta");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    setCategories((c) => c.filter((x) => x.id !== id));
    setDishes((d) => d.filter((x) => x.category_id !== id));
    toast.success("Categoria eliminata");
  };
  const renameCategory = async (cat) => {
    const name = prompt("Nuovo nome categoria", cat.name);
    if (!name) return;
    const { data } = await api.put(`/categories/${cat.id}`, { name, order: cat.order, image_path: cat.image_path || null });
    setCategories((c) => c.map((x) => (x.id === cat.id ? { ...data, menu_id: cat.menu_id } : x)));
  };

  const setCategoryPhoto = async (cat, file) => {
    try {
      let image_path = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_path = data.path;
      }
      const { data } = await api.put(`/categories/${cat.id}`, { name: cat.name, order: cat.order, image_path });
      setCategories((c) => c.map((x) => (x.id === cat.id ? { ...data, menu_id: cat.menu_id } : x)));
      toast.success(image_path ? "Foto categoria aggiornata" : "Foto categoria rimossa");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setCategoryStation = async (cat, station) => {
    try {
      const { data } = await api.put(`/categories/${cat.id}`, { name: cat.name, order: cat.order, image_path: cat.image_path || null, station });
      setCategories((c) => c.map((x) => (x.id === cat.id ? { ...data, menu_id: cat.menu_id } : x)));
      toast.success(`"${cat.name}" → ${{ kitchen: "Cucina", bar: "Bar", both: "Cucina + Bar" }[station]}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };


  const toggleAvailable = async (d) => {
    try {
      const { data } = await api.put(`/dishes/${d.id}`, { ...d, available: !d.available });
      setDishes((ds) => ds.map((x) => (x.id === d.id ? data : x)));
      toast.success(data.available ? `"${d.name}" visibile nel menù` : `"${d.name}" nascosto (terminato)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveDish = (saved, isNew) => {
    setDishes((d) => (isNew ? [...d, saved] : d.map((x) => (x.id === saved.id ? saved : x))));
  };
  const deleteDish = async (id) => {
    await api.delete(`/dishes/${id}`);
    setDishes((d) => d.filter((x) => x.id !== id));
    toast.success("Piatto eliminato");
  };

  const handleReorderCategories = (arr) => {
    const others = categories.filter((c) => c.menu_id !== activeMenu);
    setCategories([...others, ...arr.map((c, i) => ({ ...c, order: i }))]);
    api.put("/reorder/categories", { ids: arr.map((c) => c.id) }).catch((e) => { logError("Riordino categorie fallito:", e); toast.error("Riordino non salvato"); });
  };
  const handleReorderDishes = (catId, arr) => {
    const others = dishes.filter((d) => d.category_id !== catId);
    setDishes([...others, ...arr.map((d, i) => ({ ...d, order: i }))]);
    api.put("/reorder/dishes", { ids: arr.map((d) => d.id) }).catch((e) => { logError("Riordino piatti fallito:", e); toast.error("Riordino non salvato"); });
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  return (
    <div className="space-y-8" data-testid="menu-builder">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-4xl">Costruttore menù</h1>
          <p className="text-gray-500 mt-1">Organizza i tuoi menù in categorie e piatti.</p>
        </div>
        {isPaid && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => canTranslate ? setTranslateOpen(true) : toast.error("La traduzione del menù è disponibile con i piani Pro e Business")} className="rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="ai-translate-btn"><Languages className="h-4 w-4 mr-1.5 text-[#5aa469]" />Traduci</Button>
          <Button variant="outline" onClick={() => setSuggestOpen(true)} className="rounded-full border-gray-300 bg-white hover:bg-gray-50" data-testid="ai-suggest-btn"><Sparkles className="h-4 w-4 mr-1.5 text-[#5aa469]" />Assistente AI</Button>
        </div>
        )}
      </div>

      {!isPaid && <UpgradeNotice testid="free-builder-notice" text="Piano Free: puoi creare categorie, piatti e allergeni e scaricare il QR Code. Foto dei piatti e assistente AI sono disponibili dal piano Basic." />}

      {draft && (
        <div className="rounded-2xl border border-[#5aa469]/40 bg-[#5aa469]/8 p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="ai-draft-banner">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#5aa469] flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-white" /></div>
            <div>
              <p className="font-semibold">Hai un menù generato con l'AI pronto da importare</p>
              <p className="text-sm text-gray-500">{(draft.categories || []).length} categorie · {(draft.categories || []).reduce((n, c) => n + (c.dishes?.length || 0), 0)} piatti verranno aggiunti al menù selezionato.</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="ghost" onClick={dismissDraft} className="hover:bg-gray-100" data-testid="ai-draft-dismiss">Ignora</Button>
            <Button onClick={importDraft} disabled={importing} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="ai-draft-import">{importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Importa nel menù"}</Button>
          </div>
        </div>
      )}

      {/* Menu tabs */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="menu-tabs">        {menus.map((m) => (
          <button key={m.id} onClick={() => setActiveMenu(m.id)} data-testid={`menu-tab-${m.id}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${activeMenu === m.id ? "bg-[#5aa469] text-white" : m.is_foodtruck ? "bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {m.is_foodtruck && <Truck className="h-3.5 w-3.5" />}{m.name}
          </button>
        ))}
        {menus.filter((m) => !m.is_foodtruck).length < (feats.menu_limit || 1) ? (
          <Button variant="outline" size="sm" onClick={addMenu} className="rounded-full border-dashed border-gray-300 bg-white hover:bg-gray-50" data-testid="add-menu-btn">
            <Plus className="h-4 w-4 mr-1" />Nuovo menù
          </Button>
        ) : (
          <Link to="/dashboard/billing" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#4a8a56] transition-colors" data-testid="menu-limit-note">
            <Lock className="h-3 w-3" /> {(feats.menu_limit || 1) <= 1 ? "Il tuo piano include 1 menù. Più menù con Pro (2) e Business (illimitati)" : "Limite menù del piano raggiunto: passa a Business per menù illimitati"}
          </Link>
        )}
      </div>

      {current && (
        <div className="rounded-2xl mo-card p-5 flex flex-col lg:flex-row lg:items-center gap-3 justify-between flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold text-xl">{current.name}</span>
            {current.is_foodtruck && <span className="text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 inline-flex items-center gap-1" data-testid="foodtruck-menu-badge"><Truck className="h-3 w-3" /> Menù FoodTruck · solo consultabile, non ordinabile</span>}
            <Button variant="ghost" size="icon" onClick={renameMenu} className="hover:bg-gray-100" data-testid="rename-menu-btn"><Pencil className="h-4 w-4 text-gray-500" /></Button>
            {current.is_foodtruck && <Button variant="ghost" size="sm" onClick={duplicateMenu} className="hover:bg-amber-50 text-amber-800 h-8 px-2" data-testid="duplicate-menu-btn"><CopyPlus className="h-4 w-4 mr-1" />Duplica per una nuova serata</Button>}
            {menus.length > 1 && <Button variant="ghost" size="icon" onClick={deleteMenu} className="hover:bg-gray-100" data-testid="delete-menu-btn"><Trash2 className="h-4 w-4 text-red-500" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-[#5aa469]" />
            <span className="text-sm text-gray-500">Design</span>
            <Select value={current.template} onValueChange={changeTemplate}>
              <SelectTrigger className="h-10 w-40 rounded-lg" data-testid="template-select" disabled={!feats.templates}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TEMPLATE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            {!feats.templates && <Lock className="h-4 w-4 text-gray-400" />}
          </div>
          <AccentColorPicker enabled={!!feats.accent_color} />
          <HeadingFontPicker enabled={!!feats.heading_font} />
        </div>
      )}

      <div className="rounded-2xl mo-card p-5 flex gap-3">
        <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} placeholder="Nuova categoria (es. Antipasti)" className="h-11 rounded-lg focus:border-[#5aa469]" data-testid="new-category-input" />
        <Button onClick={addCategory} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold shrink-0" data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" />Categoria</Button>
      </div>

      {menuCategories.length === 0 && (
        <div className="text-center py-16 rounded-2xl border border-dashed border-gray-300 bg-white">
          <Utensils className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nessuna categoria in questo menù. Aggiungine una per iniziare.</p>
        </div>
      )}

      {menuCategories.length > 1 && (
        <OptionBanners testid="category-banners" cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          items={menuCategories.map((c) => ({ label: c.name, hint: `${dishes.filter((d) => d.category_id === c.id).length} piatti`, icon: FolderOpen, anchor: `category-${c.id}` }))} />
      )}
      <Reorder.Group axis="y" values={menuCategories} onReorder={handleReorderCategories} className="space-y-6">
        {menuCategories.map((cat) => (
          <CategoryCard key={cat.id} cat={cat} dishes={dishes.filter((d) => d.category_id === cat.id)} currency={restaurant?.currency_code || restaurant?.currency}
            onRename={() => renameCategory(cat)} onDelete={() => deleteCategory(cat.id)} onAddDish={() => setDishModal({ category_id: cat.id })}
            canPhoto={!!feats.category_photos} onPhoto={(file) => setCategoryPhoto(cat, file)}
            canStation={!!feats.tables} onStation={(station) => setCategoryStation(cat, station)}
            onEditDish={(d) => setDishModal(d)} onDeleteDish={deleteDish} onToggleDish={toggleAvailable} isPaid={isPaid} onReorderDishes={(arr) => handleReorderDishes(cat.id, arr)} />
        ))}
      </Reorder.Group>

      {dishModal && (
        <DishModal categories={menuCategories} initial={dishModal} onClose={() => setDishModal(null)} onSaved={saveDish} currency={restaurant?.currency} feats={feats} isPaid={isPaid} />
      )}
      {translateOpen && <TranslateDialog onClose={() => setTranslateOpen(false)} />}
      {suggestOpen && <SuggestDialog menuId={activeMenu} currency={restaurant?.currency_code || "eur"} onClose={() => setSuggestOpen(false)} onApplied={load} />}
    </div>
  );
}

function CategoryCard({ cat, dishes, currency, onRename, onDelete, onAddDish, onEditDish, onDeleteDish, onToggleDish, isPaid, onReorderDishes, canPhoto, onPhoto, canStation, onStation }) {
  const controls = useDragControls();
  const fileRef = useRef(null);
  return (
    <Reorder.Item value={cat} dragListener={false} dragControls={controls} id={`category-${cat.id}`} className="rounded-2xl mo-card overflow-hidden bg-white scroll-mt-24" data-testid={`category-${cat.id}`}>
      {canPhoto && cat.image_path && <img src={fileUrl(cat.image_path)} alt="" className="h-28 w-full object-cover" data-testid={`cat-photo-${cat.id}`} />}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/70">
        <div className="flex items-center gap-2">
          <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing touch-none text-gray-300 hover:text-gray-500" data-testid={`drag-cat-${cat.id}`} aria-label="Trascina categoria"><GripVertical className="h-4 w-4" /></button>
          <h3 className="font-display font-semibold text-2xl">{cat.name}</h3>
          <span className="text-xs text-gray-400">({dishes.length})</span>
        </div>
        <div className="flex items-center gap-1">
          {canPhoto && (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onPhoto(e.target.files[0]); e.target.value = ""; }} data-testid={`cat-photo-input-${cat.id}`} />
              <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} title={cat.image_path ? "Cambia foto categoria" : "Aggiungi foto categoria"} className="hover:bg-gray-100" data-testid={`photo-cat-${cat.id}`}><Camera className={`h-4 w-4 ${cat.image_path ? "text-[#4a8a56]" : "text-gray-400"}`} /></Button>
              {cat.image_path && <Button variant="ghost" size="icon" onClick={() => onPhoto(null)} title="Rimuovi foto" className="hover:bg-gray-100" data-testid={`remove-cat-photo-${cat.id}`}><ImageOff className="h-4 w-4 text-gray-400" /></Button>}
            </>
          )}
          {canStation && (
            <select value={cat.station || "kitchen"} onChange={(e) => onStation(e.target.value)} title="Destinazione stampa comanda" className="h-8 rounded-full border border-gray-200 bg-white px-2 text-xs text-gray-600 mr-1" data-testid={`station-cat-${cat.id}`}>
              <option value="kitchen">Cucina</option><option value="bar">Bar</option><option value="both">Cucina + Bar</option>
            </select>
          )}
          <Button variant="ghost" size="icon" onClick={onRename} className="hover:bg-gray-100" data-testid={`rename-cat-${cat.id}`}><Pencil className="h-4 w-4 text-gray-500" /></Button>
          <Button variant="ghost" size="icon" onClick={onDelete} className="hover:bg-gray-100" data-testid={`delete-cat-${cat.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      </div>
      <Reorder.Group axis="y" values={dishes} onReorder={onReorderDishes} className="divide-y divide-gray-100">
        {dishes.map((d) => <DishRow key={d.id} d={d} currency={currency} isPaid={isPaid} onEdit={() => onEditDish(d)} onDelete={() => onDeleteDish(d.id)} onToggle={() => onToggleDish(d)} />)}
      </Reorder.Group>
      <div className="px-5 py-3">
        <Button variant="ghost" onClick={onAddDish} className="text-[#5aa469] hover:bg-[#5aa469]/10" data-testid={`add-dish-${cat.id}`}><Plus className="h-4 w-4 mr-1" />Aggiungi piatto</Button>
      </div>
    </Reorder.Item>
  );
}

function DishRow({ d, currency, onEdit, onDelete, onToggle, isPaid }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={d} dragListener={false} dragControls={controls} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 px-4 sm:px-5 py-3 hover:bg-gray-50/60 bg-white" data-testid={`dish-${d.id}`}>
      <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing touch-none text-gray-300 hover:text-gray-500 shrink-0" data-testid={`drag-dish-${d.id}`} aria-label="Trascina piatto"><GripVertical className="h-4 w-4" /></button>
      <div className="h-14 w-14 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
        {d.image_path ? <img src={fileUrl(d.image_path)} alt="" className="h-full w-full object-cover" /> : <Utensils className="h-5 w-5 text-gray-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-gray-900 truncate">{d.name}</p>
          {!d.available && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Nascosto</span>}
          {d.orderable && <span className="text-xs text-[#4a8a56] bg-[#5aa469]/10 px-2 py-0.5 rounded-full">Ordinabile</span>}
          {d.variants?.length > 0 && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{d.variants.length} varianti</span>}
        </div>
        {d.description && <p className="text-sm text-gray-500 truncate">{d.description}</p>}
        <AllergenBadges codes={d.allergens} compact />
      </div>
      <p className="font-semibold text-gray-900 whitespace-nowrap">{d.variants?.length > 0 ? `da ${fmtMoney(Math.min(...d.variants.map((v) => Number(v.price))), currency)}` : fmtMoney(d.price, currency)}</p>
      <div className="flex items-center gap-1 w-full sm:w-auto justify-end order-last sm:order-none">
        <Button variant="ghost" size="icon" onClick={onToggle} title={d.available ? "Nascondi (terminato)" : "Mostra nel menù"} className="hover:bg-gray-100" data-testid={`toggle-dish-${d.id}`}>
          {d.available ? <Eye className="h-4 w-4 text-gray-500" /> : <EyeOff className="h-4 w-4 text-amber-600" />}
        </Button>
        {isPaid && <Button variant="ghost" size="icon" onClick={onEdit} title={d.image_path ? "Cambia foto" : "Aggiungi foto"} className="hover:bg-gray-100" data-testid={`photo-dish-${d.id}`}><Camera className={`h-4 w-4 ${d.image_path ? "text-[#4a8a56]" : "text-gray-400"}`} /></Button>}
        <Button variant="ghost" size="icon" onClick={onEdit} className="hover:bg-gray-100" data-testid={`edit-dish-${d.id}`}><Pencil className="h-4 w-4 text-gray-500" /></Button>
        <Button variant="ghost" size="icon" onClick={onDelete} className="hover:bg-gray-100" data-testid={`delete-dish-${d.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
    </Reorder.Item>
  );
}

const rowKey = () => Math.random().toString(36).slice(2, 10);
const withKeys = (arr) => (arr || []).map((x) => ({ ...x, _k: x._k || rowKey(), ...(x.choices ? { choices: withKeys(x.choices) } : {}) }));

function DishModal({ categories, initial, onClose, onSaved, currency, feats, isPaid = true }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    category_id: initial.category_id || categories[0]?.id,
    name: initial.name || "",
    description: initial.description || "",
    price: initial.price ?? 0,
    image_path: initial.image_path || null,
    available: initial.available ?? true,
    variants: withKeys(initial.variants),
    orderable: initial.orderable ?? false,
    counts_capacity: initial.counts_capacity ?? true,
    options: withKeys(initial.options),
    allergens: initial.allergens || [],
    tags: initial.tags || [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAllLoading, setAiAllLoading] = useState(false);
  const [aiSuggested, setAiSuggested] = useState(null);
  const suggestAllergens = async () => {
    if (!form.name.trim()) { toast.error("Inserisci prima il nome del piatto"); return; }
    setAiAllLoading(true);
    try {
      const { data } = await api.post("/ai/dish-allergens", { name: form.name, description: form.description });
      setAiSuggested(data.allergens);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiAllLoading(false); }
  };
  const fileRef = useRef();
  const cameraRef = useRef();

  const genDesc = async () => {
    if (!form.name.trim()) { toast.error("Inserisci prima il nome del piatto"); return; }
    setAiLoading(true);
    try {
      const cat = categories.find((c) => c.id === form.category_id)?.name || "";
      const { data } = await api.post("/ai/dish-description", { name: form.name, category: cat });
      setForm((f) => ({ ...f, description: data.description }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Immagine caricata");
    } catch (err) { toast.error("Upload fallito"); }
    finally { setUploading(false); }
  };

  // variants (Business)
  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { label: "", price: 0, _k: rowKey() }] }));
  const setVariant = (i, k, v) => setForm((f) => ({ ...f, variants: f.variants.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const rmVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));

  // option groups (Pro/Business ordering)
  const addGroup = () => setForm((f) => ({ ...f, options: [...f.options, { name: "", required: false, _k: rowKey(), choices: [{ label: "", price_delta: 0, _k: rowKey() }] }] }));
  const setGroup = (gi, k, v) => setForm((f) => ({ ...f, options: f.options.map((g, j) => (j === gi ? { ...g, [k]: v } : g)) }));
  const rmGroup = (gi) => setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== gi) }));
  const addChoice = (gi) => setForm((f) => ({ ...f, options: f.options.map((g, j) => (j === gi ? { ...g, choices: [...g.choices, { label: "", price_delta: 0, _k: rowKey() }] } : g)) }));
  const setChoice = (gi, ci, k, v) => setForm((f) => ({ ...f, options: f.options.map((g, j) => (j === gi ? { ...g, choices: g.choices.map((c, l) => (l === ci ? { ...c, [k]: v } : c)) } : g)) }));
  const rmChoice = (gi, ci) => setForm((f) => ({ ...f, options: f.options.map((g, j) => (j === gi ? { ...g, choices: g.choices.filter((_, l) => l !== ci) } : g)) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome del piatto"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        variants: (feats.variants ? form.variants : []).filter((v) => v.label.trim()).map((v) => ({ label: v.label.trim(), price: parseFloat(v.price) || 0 })),
        options: (feats.ordering ? form.options : []).filter((g) => g.name.trim()).map((g) => ({ name: g.name.trim(), required: !!g.required, choices: g.choices.filter((c) => c.label.trim()).map((c) => ({ label: c.label.trim(), price_delta: parseFloat(c.price_delta) || 0 })) })),
        orderable: feats.ordering ? form.orderable : false,
        counts_capacity: form.counts_capacity,
      };
      const res = isNew ? await api.post("/dishes", payload) : await api.put(`/dishes/${initial.id}`, payload);
      onSaved(res.data, isNew);
      toast.success(isNew ? "Piatto aggiunto" : "Piatto aggiornato");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="dish-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "Nuovo piatto" : "Modifica piatto"}</DialogTitle>
          <DialogDescription>Compila i dettagli del piatto e salva.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isPaid && (
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {form.image_path ? <img src={fileUrl(form.image_path)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6 text-gray-300" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="dish-image-input" />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="dish-camera-input" />
              <Button variant="outline" size="sm" className="rounded-full border-gray-300 bg-white hover:bg-gray-50 sm:hidden" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="dish-camera-btn">
                <Camera className="h-4 w-4 mr-1.5" /> Scatta foto
              </Button>
              <Button variant="outline" size="sm" className="rounded-full border-gray-300 bg-white hover:bg-gray-50" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dish-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />} Foto piatto
              </Button>
              {form.image_path && <Button variant="ghost" size="sm" className="text-red-500 ml-1 hover:bg-gray-100" onClick={() => setForm((f) => ({ ...f, image_path: null }))}><X className="h-4 w-4" /></Button>}
            </div>
          </div>
          )}
          <div>
            <Label>Categoria</Label>
            <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
              <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="dish-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nome</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="Es. Spaghetti alle vongole" data-testid="dish-name-input" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Descrizione</Label>
              {isPaid && (
              <Button type="button" variant="ghost" size="sm" onClick={genDesc} disabled={aiLoading} className="h-7 px-2 text-[#5aa469] hover:bg-[#5aa469]/10" data-testid="ai-desc-btn">
                {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />} Genera con AI
              </Button>
              )}
            </div>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 rounded-lg focus:border-[#5aa469]" placeholder="Ingredienti, preparazione…" data-testid="dish-desc-input" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Allergeni</Label>
              {feats.ai_allergens && (
                <Button type="button" variant="ghost" size="sm" onClick={suggestAllergens} disabled={aiAllLoading} className="h-7 px-2 text-[#5aa469] hover:bg-[#5aa469]/10" data-testid="ai-allergens-btn">
                  {aiAllLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />} Suggerisci con AI
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-2">Seleziona gli allergeni presenti (Reg. UE 1169/2011). Verranno mostrati sotto il piatto nel menù.</p>
            {aiSuggested && (
              <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/70 p-2.5 text-xs text-amber-800 flex items-center justify-between gap-2 flex-wrap" data-testid="ai-allergens-suggestion">
                <span>{aiSuggested.length ? <>L'AI suggerisce: <strong>{aiSuggested.map(allergenLabel).join(", ")}</strong></> : "L'AI non rileva allergeni evidenti."}</span>
                {aiSuggested.length > 0 && (
                  <div className="flex gap-1">
                    <Button type="button" size="sm" onClick={() => { setForm((f) => ({ ...f, allergens: Array.from(new Set([...f.allergens, ...aiSuggested])) })); setAiSuggested(null); }} className="h-7 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid="ai-allergens-apply">Conferma</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setAiSuggested(null)} className="h-7 text-xs" data-testid="ai-allergens-dismiss">Ignora</Button>
                  </div>
                )}
              </div>
            )}
            <AllergenPicker value={form.allergens} onChange={(allergens) => setForm((f) => ({ ...f, allergens }))} />
          </div>
          {feats.diet_tags && (
            <div>
              <Label>Preferenze alimentari</Label>
              <p className="text-xs text-gray-400 mb-2">Etichette mostrate accanto al piatto; i clienti possono filtrare il menù.</p>
              <DietTagPicker value={form.tags} onChange={(tags) => setForm((f) => ({ ...f, tags }))} />
            </div>
          )}
          <div>
            <Label>Prezzo base ({currency})</Label>
            <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" data-testid="dish-price-input" />
            {form.variants.length > 0 && <p className="text-xs text-gray-400 mt-1">Con le varianti attive, il prezzo mostrato parte dalla variante più economica.</p>}
          </div>

          {/* Variants — Business */}
          {feats.variants && (
            <div className="rounded-xl border border-gray-200 p-4" data-testid="variants-section">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-900">Prezzi multipli / varianti</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addVariant} className="h-7 text-[#5aa469] hover:bg-[#5aa469]/10" data-testid="add-variant-btn"><Plus className="h-3.5 w-3.5 mr-1" />Variante</Button>
              </div>
              <div className="space-y-2">
                {form.variants.map((v, i) => (
                  <div key={v._k} className="flex gap-2 items-center" data-testid={`variant-row-${i}`}>
                    <Input value={v.label} onChange={(e) => setVariant(i, "label", e.target.value)} placeholder="Es. Piccola" className="h-10 rounded-lg" data-testid={`variant-label-${i}`} />
                    <Input type="number" step="0.01" min="0" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} placeholder="Prezzo" className="h-10 w-28 rounded-lg" data-testid={`variant-price-${i}`} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => rmVariant(i)} className="text-red-500 hover:bg-gray-100"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                {form.variants.length === 0 && <p className="text-xs text-gray-400">Aggiungi taglie o formati (es. Piccola/Media/Grande) con prezzi diversi.</p>}
              </div>
            </div>
          )}

          {/* Ordering + options — Pro/Business */}
          {feats.ordering && (
            <div className="rounded-xl border border-gray-200 p-4 space-y-3" data-testid="ordering-section">
              <div className="flex items-center justify-between">
                <Label className="text-gray-900">Ordinabile dal cliente</Label>
                <Switch checked={form.orderable} onCheckedChange={(v) => setForm((f) => ({ ...f, orderable: v }))} data-testid="dish-orderable-switch" />
              </div>
              {form.orderable && feats.takeaway && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-gray-900">Conta nella capacità asporto</Label>
                    <p className="text-xs text-gray-400">Attivo per pizze e piatti da preparare; disattiva per bibite e prodotti pronti.</p>
                  </div>
                  <Switch checked={form.counts_capacity} onCheckedChange={(v) => setForm((f) => ({ ...f, counts_capacity: v }))} data-testid="dish-capacity-switch" />
                </div>
              )}
              {form.orderable && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Personalizzazioni (facoltative)</span>
                    <Button type="button" variant="ghost" size="sm" onClick={addGroup} className="h-7 text-[#5aa469] hover:bg-[#5aa469]/10" data-testid="add-option-group-btn"><Plus className="h-3.5 w-3.5 mr-1" />Gruppo</Button>
                  </div>
                  <div className="space-y-3">
                    {form.options.map((g, gi) => (
                      <div key={g._k} className="rounded-lg bg-gray-50 p-3 space-y-2" data-testid={`option-group-${gi}`}>
                        <div className="flex gap-2 items-center">
                          <Input value={g.name} onChange={(e) => setGroup(gi, "name", e.target.value)} placeholder="Es. Cottura" className="h-9 rounded-lg" data-testid={`option-group-name-${gi}`} />
                          <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap"><input type="checkbox" checked={g.required} onChange={(e) => setGroup(gi, "required", e.target.checked)} />Obbl.</label>
                          <Button type="button" variant="ghost" size="icon" onClick={() => rmGroup(gi)} className="text-red-500 hover:bg-gray-100"><X className="h-4 w-4" /></Button>
                        </div>
                        {g.choices.map((c, ci) => (
                          <div key={c._k} className="flex gap-2 items-center pl-2">
                            <Input value={c.label} onChange={(e) => setChoice(gi, ci, "label", e.target.value)} placeholder="Opzione" className="h-9 rounded-lg" />
                            <Input type="number" step="0.01" value={c.price_delta} onChange={(e) => setChoice(gi, ci, "price_delta", e.target.value)} placeholder="+€" className="h-9 w-24 rounded-lg" />
                            <Button type="button" variant="ghost" size="icon" onClick={() => rmChoice(gi, ci)} className="text-red-400 hover:bg-gray-100"><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" onClick={() => addChoice(gi)} className="h-7 text-gray-500 hover:bg-gray-100 ml-2"><Plus className="h-3.5 w-3.5 mr-1" />Opzione</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label>Visibile nel menù</Label>
            <Switch checked={form.available} onCheckedChange={(v) => setForm((f) => ({ ...f, available: v }))} data-testid="dish-available-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="hover:bg-gray-100">Annulla</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="dish-save-btn">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TranslateDialog({ onClose }) {
  const [lang, setLang] = useState("Inglese");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/translate", { target_lang: lang });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="translate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Traduci il menù con AI</DialogTitle>
          <DialogDescription>Anteprima della traduzione. Il menù originale non viene modificato.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-11 rounded-lg" data-testid="translate-lang"><SelectValue /></SelectTrigger>
            <SelectContent>{["Inglese", "Francese", "Spagnolo", "Tedesco"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={run} disabled={loading} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold shrink-0" data-testid="translate-run">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Traduci"}</Button>
        </div>
        {result && (
          <div className="space-y-4 mt-2" data-testid="translate-result">
            {result.categories.map((c) => (
              <div key={c.id}>
                <p className="font-semibold text-[#4a8a56] mb-1">{c.name}</p>
                {result.dishes.filter((d) => d.category_id === c.id).map((d) => (
                  <div key={d.id} className="text-sm py-1.5 border-b border-gray-100">
                    <span className="font-medium text-gray-900">{d.name}</span>
                    {d.description && <span className="text-gray-500"> — {d.description}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuggestDialog({ onClose, onApplied, menuId, currency }) {
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/suggest", { description: desc });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      for (const c of result.categories) {
        const { data: cat } = await api.post("/categories", { name: c.name, menu_id: menuId });
        for (const d of (c.dishes || [])) {
          await api.post("/dishes", { category_id: cat.id, name: d.name, description: d.description || "", price: Number(d.price) || 0, available: true });
        }
      }
      toast.success("Menù aggiunto al tuo ristorante!");
      onApplied?.();
      onClose();
    } catch (e) { toast.error("Errore durante l'aggiunta al menù"); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="suggest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Assistente AI menù</DialogTitle>
          <DialogDescription>Descrivi la tua attività: l'AI proporrà categorie e piatti che puoi aggiungere con un click.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Es. Pizzeria napoletana con forno a legna e birre artigianali" className="rounded-lg focus:border-[#5aa469]" data-testid="suggest-input" />
          <Button onClick={run} disabled={loading} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold self-start" data-testid="suggest-run">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Genera suggerimenti"}</Button>
        </div>
        {result && (
          <div className="space-y-4 mt-2" data-testid="suggest-result">
            {result.categories.map((c, ci) => (
              <div key={`${c.name}-${ci}`}>
                <p className="font-semibold text-[#4a8a56] mb-1">{c.name}</p>
                {(c.dishes || []).map((d, di) => (
                  <div key={`${d.name}-${di}`} className="text-sm py-1.5 border-b border-gray-100 flex justify-between gap-3">
                    <span><span className="font-medium text-gray-900">{d.name}</span>{d.description && <span className="text-gray-500"> — {d.description}</span>}</span>
                    <span className="text-[#5aa469] font-semibold whitespace-nowrap">{fmtMoney(d.price || 0, currency)}</span>
                  </div>
                ))}
              </div>
            ))}
            <Button onClick={apply} disabled={applying} className="rounded-full w-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="suggest-apply">{applying ? "Aggiunta…" : "Aggiungi al mio menù"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
